import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal, Row, Col, Spinner, Badge, Card } from "react-bootstrap";
import { LanguageProvider } from "../../../core/language-provider";
import { ViewManager } from "../../../core/view-manager";
import { DataManager } from "../../../core/data-manager";
import { toast } from "react-toastify";
import { settings } from "../../../config/settings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * ModalEntityConfigDnDNative
 * - Sinistra: colonne selezionate (ordinabili via drag&drop nativo HTML5)
 * - Destra: tutte le colonne (mostra quali sono "In uso" e quali sono "Disponibili")
 *
 * Interazioni:
 *  - Trascina da destra -> sinistra per aggiungere in una posizione specifica
 *  - Trascina da sinistra -> destra per rimuovere
 *  - Trascina dentro la sinistra per riordinare
 *  - Pulsanti "+" e "-" come fallback al drag&drop
 */
export default function ModalEntityConfigDnDNative({ entity, props, entityPreferences, status, close }) {

  const [saving, setSaving] = useState(false);
  const [allColumns, setAllColumns] = useState([]);
  const [selected, setSelected] = useState([]);
  const [newFields, setNewFields] = useState(null);

  // Stato UI drag&drop
  const [dragging, setDragging] = useState(null); // { item, source: 'selected'|'available', index }
  const [overList, setOverList] = useState(null); // 'selected' | 'available' | null
  const [placeholderIndex, setPlaceholderIndex] = useState(null); // indice di inserimento in selected

  // Inizializzazione
  useEffect(() => {
    if (!entity) return;
    const cols = (ViewManager.getColumns(entity) || []).slice();
    const prefSelected = (entityPreferences?.columns || []).filter((c) => cols.includes(c));
    setAllColumns(cols);
    setSelected(prefSelected);
    setNewFields({ columns: prefSelected });
  }, [entity, entityPreferences]);

  // Helpers stato
  const syncSelected = useCallback((next) => {
    setSelected(next);
    setNewFields({ columns: next });
  }, []);

  const addColumn = useCallback((col, atIndex = null) => {
    if (!col) return;
    if (selected.includes(col)) return;
    const insertIndex = atIndex === null ? selected.length : Math.max(0, Math.min(atIndex, selected.length));
    const next = [...selected];
    next.splice(insertIndex, 0, col);
    syncSelected(next);
  }, [selected, syncSelected]);

  const removeColumn = useCallback((col) => {
    if (!selected.includes(col)) return;
    syncSelected(selected.filter((c) => c !== col));
  }, [selected, syncSelected]);

  const reorderSelected = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const list = [...selected];
    const [moved] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, moved);
    syncSelected(list);
  }, [selected, syncSelected]);

  // Drag & Drop native: start
  const onDragStartItem = (e, payload) => {
    const json = JSON.stringify(payload);
    // Alcuni browser richiedono sempre text/plain
    e.dataTransfer.setData("text/plain", json);
    e.dataTransfer.setData("application/json", json);
    // Effetto visivo
    e.dataTransfer.effectAllowed = "move";
    setDragging(payload); // { item, source, index }
  };

  const clearDnDState = () => {
    setDragging(null);
    setOverList(null);
    setPlaceholderIndex(null);
  };

  // Calcolo placeholderIndex quando si trascina sopra un item della lista "selected"
  const onDragOverSelectedItem = (e, itemIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setOverList("selected");
    const rect = e.currentTarget.getBoundingClientRect();
    const isTopHalf = e.clientY < rect.top + rect.height / 2;
    setPlaceholderIndex(isTopHalf ? itemIndex : itemIndex + 1);
    e.dataTransfer.dropEffect = "move";
  };

  // Drag over su contenitore selected (quando non ci sono item)
  const onDragOverSelectedList = (e) => {
    e.preventDefault();
    setOverList("selected");
    // se la lista è vuota o non siamo sopra un item, default a fine lista
    setPlaceholderIndex(selected.length);
    e.dataTransfer.dropEffect = "move";
  };

  // Drag over su available per consentire drop (rimozione)
  const onDragOverAvailable = (e) => {
    e.preventDefault();
    setOverList("available");
    e.dataTransfer.dropEffect = "move";
  };

  // Drop sulla lista selected
  const onDropSelected = (e) => {
    e.preventDefault();
    e.stopPropagation();
    let data = null;
    try {
      const raw = e.dataTransfer.getData("application/json") || e.dataTransfer.getData("text/plain");
      data = JSON.parse(raw);
    } catch (_) { }

    const dropIndex = placeholderIndex == null ? selected.length : Math.max(0, Math.min(placeholderIndex, selected.length));

    if (!data) {
      clearDnDState();
      return;
    }

    // Dalle available -> selected (aggiungi)
    if (data.source === "available") {
      if (!selected.includes(data.item)) {
        addColumn(data.item, dropIndex);
      }
      clearDnDState();
      return;
    }

    // Da selected -> selected (riordino)
    if (data.source === "selected") {
      const from = data.index;
      let to = dropIndex;
      // quando trasciniamo verso il basso e rimuoviamo la posizione originale, l'indice target va aggiustato
      if (to > from) to = to - 1;
      to = Math.max(0, Math.min(to, selected.length - 1));
      reorderSelected(from, to);
      clearDnDState();
      return;
    }

    clearDnDState();
  };

  // Drop su available: rimuove se proveniva da selected
  const onDropAvailable = (e) => {
    e.preventDefault();
    e.stopPropagation();
    let data = null;
    try {
      const raw = e.dataTransfer.getData("application/json") || e.dataTransfer.getData("text/plain");
      data = JSON.parse(raw);
    } catch (_) { }

    if (data && data.source === "selected") {
      removeColumn(data.item);
    }

    clearDnDState();
  };

  function closeModal() {
    close(entityPreferences);
  }

  async function savePreferences() {
    setSaving(true)
    try {
      const p = newFields;
      const conditions = [
        {
          fieldName: "entity",
          type: "value",
          value: entity,
        },
      ];

      const response = await DataManager.searchItems(
        "user_entity_preferences",
        1,
        1,
        { field: "id", order: "ASC" },
        "",
        [],
        conditions
      );

      const body = { content: JSON.stringify(p), entity };

      if (response.success === 1 && response.body && response.body.length) {
        const id = response.body[0].id;
        const response2 = await DataManager.updateItem("user_entity_preferences", body, id);
        if (response2.success === 1) {
          toast.success("Preferenze aggiornate con successo.", { theme: "colored" });
        } else {
          toast.error("Non è stato possibile salvare le preferenze.", { theme: "colored" });
        }
      } else {
        const response2 = await DataManager.insertItem("user_entity_preferences", body);
        if (response2.success === 1) {
          toast.success("Preferenze salvate con successo.", { theme: "colored" });
        } else {
          toast.error("Non è stato possibile salvare le preferenze.", { theme: "colored" });
        }
      }

      close(newFields);

    } catch (err) {
      toast.error("Errore durante il salvataggio delle preferenze.", { theme: "colored" });
    }
    setSaving(false)
  }

  // Render guard
  if (props === null || status === false || newFields === null) return null;

  const modalSize =
    settings.modalDefaultSize && settings.modalDefaultSize.entityConfig
      ? settings.modalDefaultSize.entityConfig
      : "xl";

  const isDraggingSelected = dragging?.source === "selected";

  return (
    <Modal show={status} onHide={closeModal} size={modalSize} scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {LanguageProvider.get("defaults.options")} - {LanguageProvider.get("entity." + entity)}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row>
          {/* Selezionate (sinistra) */}
          <Col lg={6} className="mb-3">
            <Card>
              <Card.Header className="d-flex align-items-center justify-content-between">
                <span>
                  <strong>{LanguageProvider.get("defaults.chosen_columns")}</strong> <Badge bg="secondary">{selected.length}</Badge>
                </span>
              </Card.Header>

              <div
                onDragOver={onDragOverSelectedList}
                onDrop={onDropSelected}
                onDragLeave={() => {
                  setOverList(null);
                  setPlaceholderIndex(null);
                }}
                className={`list-group rounded-0 ${overList === "selected" ? "bg-light" : ""}`}
                style={{ minHeight: 300, padding: 20 }}
              >
                {selected.length === 0 && (
                  <div className="list-group-item text-muted">Trascina qui le colonne da usare</div>
                )}

                {selected.map((col, index) => {
                  const label = LanguageProvider.getLabel(entity, col);
                  return (
                    <div key={col}>
                      {/* Placeholder sopra l'item quando necessario */}
                      {overList === "selected" && placeholderIndex === index && (
                        <div className="list-group-item p-0">
                          <div style={{ height: 8, borderTop: "2px dashed #6c757d" }} />
                        </div>
                      )}

                      <div
                        className="list-group-item d-flex align-items-center justify-content-between small"
                        draggable
                        onDragStart={(e) => onDragStartItem(e, { item: col, source: "selected", index })}
                        onDragOver={(e) => onDragOverSelectedItem(e, index)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted" role="img" aria-label="Drag handle">
                            <FontAwesomeIcon icon="grip-vertical" />
                          </span>
                          <span>{label}</span>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeColumn(col)}
                          aria-label="Rimuovi colonna"
                        >
                          <FontAwesomeIcon icon="minus" />
                        </Button>
                      </div>
                    </div>
                  );
                })}

                {/* Placeholder in coda */}
                {overList === "selected" && placeholderIndex === selected.length && (
                  <div className="list-group-item p-0">
                    <div style={{ height: 8, borderTop: "2px dashed #6c757d" }} />
                  </div>
                )}
              </div>
            </Card>
          </Col>

          {/* Disponibili (destra) */}
          <Col lg={6} className="mb-3">
            <Card>
              <Card.Header className="d-flex align-items-center justify-content-between">
                <span>
                  <strong>{LanguageProvider.get("defaults.available_columns")}</strong> <Badge bg="secondary">{allColumns.length}</Badge>
                </span>
              </Card.Header>

              <div
                onDragOver={onDragOverAvailable}
                onDrop={onDropAvailable}
                onDragLeave={() => setOverList(null)}
                className={`list-group rounded-0 ${overList === "available" ? "bg-light" : ""}`}
                style={{ minHeight: 300, padding: 20 }}
              >
                {allColumns.map((col) => {
                  const label = LanguageProvider.getLabel(entity, col);
                  const inUse = selected.includes(col);

                  if (inUse) {
                    return (
                      <div
                        key={col}
                        className="list-group-item d-flex align-items-center justify-content-between text-secondary small"
                        style={{ background: 'rgb(255 208 208)' }}
                      >
                        <span>{label}</span>
                        <Badge bg="secondary">{LanguageProvider.get("defaults.used")}</Badge>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={col}
                      className="list-group-item d-flex align-items-center justify-content-between text-dark small"
                      style={{ background: 'rgb(201 241 200)' }}
                      draggable
                      onDragStart={(e) => onDragStartItem(e, { item: col, source: "available", index: -1 })}
                    >
                      <span>{label}</span>
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="success">{LanguageProvider.get("defaults.available")}</Badge>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => addColumn(col)}
                          aria-label="Aggiungi colonna"
                        >
                          <FontAwesomeIcon icon="plus" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        {saving ? (
          <Button variant="primary" onClick={savePreferences}>
            <Spinner size="sm" />
          </Button>
        ) : (
          <Button variant="primary" onClick={savePreferences}>
            {LanguageProvider.get("defaults.save_preferences")}
          </Button>
        )}
        <Button variant="secondary" onClick={closeModal}>
          {LanguageProvider.get("defaults.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
